import { type Ref, ref } from 'vue'

import { useConfig } from '@/composables/config'
import type { OutOfPageAdType } from '@/modules/adv/types'
import { useLoggerStore } from '@/store/logger'
import { useMyGamesStore } from '@/store/my-games/my-games-store'
import { zipMyGame } from '@/store/my-games/utils'
import type { APIGameType } from '@/types'
import type { LoggerEvent } from '@/types/logger.types'

const ADD_GAME_TIME_MS = 15_000 // 15 sec

/**
 * Periodic work while the game is running: ping analytics events and adding the game to "my games".
 */
export function useGamePingSession(options: {
    game: Ref<APIGameType | undefined>
    isFullscreen: Ref<boolean>
    /** Set by the game frame on an empty ad result; attached to the next ping and reset. */
    lastAdResult: Ref<OutOfPageAdType | undefined>
}) {
    const { VUE_APP_GAME_PING_INTERVAL_MS } = useConfig()
    const loggerStore = useLoggerStore()
    const myGamesStore = useMyGamesStore()

    const addMyGameTimeOut = ref<number | undefined>(ADD_GAME_TIME_MS)
    const pingIntervalRef = ref<ReturnType<typeof setInterval>>()

    function ping() {
        const event: LoggerEvent = {
            event: 'custom_event',
            eventName: 'ping',
            dimension1: options.isFullscreen.value ? 'fullscreen' : 'minimized',
        }

        if (options.lastAdResult.value) {
            event.adPlacement = options.lastAdResult.value
            options.lastAdResult.value = undefined
        }
        loggerStore.logEvent(event)
    }

    function waitToAddMyGame() {
        if (addMyGameTimeOut.value === undefined) {
            return
        }
        addMyGameTimeOut.value -= VUE_APP_GAME_PING_INTERVAL_MS
        if (addMyGameTimeOut.value <= 0) {
            addMyGameTimeOut.value = undefined
            myGamesStore.update(zipMyGame(options.game.value!))
        }
    }

    function startPingSession() {
        pingIntervalRef.value = setInterval(() => {
            if (!document.hidden) {
                ping()
                waitToAddMyGame()
            }
        }, VUE_APP_GAME_PING_INTERVAL_MS)
    }

    function stopPingSession() {
        clearInterval(pingIntervalRef.value || 0)
    }

    return {
        startPingSession,
        stopPingSession,
    }
}
